import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../context/ThemeContext'
import { useIdleLogout } from './hooks/useIdleLogout'
import { NavItem } from './components/NavItem'
import { IdleWarning } from './components/IdleWarning'
import { InviteModal } from './components/InviteModal'
import { Overview } from './sections/Overview'
import { UserManagement } from './sections/UserManagement'
import { CptCisAllocation } from './sections/CptCisAllocation'
import { ReferralAnalytics } from './sections/ReferralAnalytics'
import { SeasonRewards } from './sections/SeasonRewards'
import { SeasonLeaderboardManagement } from './sections/SeasonLeaderboardManagement'
import { PlatformConfig } from './sections/PlatformConfig'
import { AdManager } from './sections/AdManager'
import { AdminTeam } from './sections/AdminTeam'
import { PolicyEditor } from './sections/PolicyEditor'
import { CommunityModeration } from './sections/CommunityModeration'
import { AuditLogManagement } from './sections/AuditLogManagement'
import { Announcements } from './sections/Announcements'
import { Categories } from './sections/Categories'
import { StakingSettings } from './sections/StakingSettings'
import { StakingManagement } from './sections/StakingManagement'
import { OfferwallManagement } from './sections/OfferwallManagement'
import { PlatformRevenue } from './sections/PlatformRevenue'
import { TaskManagement } from './sections/TaskManagement'
import { BountyManagement } from './sections/BountyManagement'
import { CampaignAnalytics } from './sections/CampaignAnalytics'

// ── DASHBOARD ─────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate               = useNavigate()
  const { logout: authLogout } = useAuth()
  const { dark, toggle: toggleTheme } = useTheme()
  const [activeTab, setActiveTab]         = useState('overview')
  const [showInvite, setShowInvite]       = useState(false)
  const [teamRefreshKey, setTeamRefreshKey] = useState(0)
  const [idleWarning, setIdleWarning]     = useState(false)
  const [idleCountdown, setIdleCountdown] = useState(60)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const countdownRef = useRef(null)

  const logout = () => { authLogout(); navigate('/login') }

  useIdleLogout(14 * 60 * 1000, () => {
    setIdleWarning(true)
    setIdleCountdown(60)
    countdownRef.current = setInterval(() => {
      setIdleCountdown(c => {
        if (c <= 1) { clearInterval(countdownRef.current); logout(); return 0 }
        return c - 1
      })
    }, 1000)
  })

  const stayActive = () => { setIdleWarning(false); clearInterval(countdownRef.current) }

  const tabs = [
    // ── Core ──
    { id: 'overview',      icon: 'dashboard',             label: 'Overview'              },
    { id: 'users',         icon: 'group',                 label: 'User Management'       },
    // ── Finance ──
    { id: 'cpt_cis',       icon: 'currency_exchange',     label: 'CPT / CIS Alloc.'      },
    { id: 'season',        icon: 'emoji_events',          label: 'Season Rewards'        },
    { id: 'season_payout', icon: 'account_balance_wallet', label: 'Season Payouts'       },
    { id: 'staking',       icon: 'savings',               label: 'Staking Mgmt'          },
    { id: 'revenue',       icon: 'payments',              label: 'Platform Revenue'      },
    // ── Engagement ──
    { id: 'referrals',     icon: 'share',                 label: 'Referral Analytics'    },
    // ── Monetisation ──
    { id: 'offerwall',     icon: 'redeem',                label: 'Offerwall Mgmt'        },
    { id: 'social_tasks',  icon: 'task_alt',              label: 'Social Tasks'          },
    { id: 'bounty',        icon: 'military_tech',         label: 'Bounty Campaigns'      },
    { id: 'ads',           icon: 'ads_click',             label: 'Ad Manager'            },
    { id: 'campaigns',     icon: 'analytics',             label: 'Campaign Analytics'    },
    // ── Moderation ──
    { id: 'moderation',    icon: 'shield',                label: 'Community Moderation'  },
    { id: 'announcements', icon: 'campaign',              label: 'Announcements'         },
    { id: 'categories',    icon: 'category',              label: 'Categories'            },
    // ── Admin ──
    { id: 'audit',         icon: 'history',               label: 'Audit Logs'            },
    { id: 'config',        icon: 'tune',                  label: 'Platform Config'       },
    { id: 'team',          icon: 'shield_person',         label: 'Admin Team'            },
    { id: 'policies',      icon: 'policy',                label: 'Policies'              },
  ]
  const tabTitles = {
    overview:      'Dashboard',
    users:         'User Management',
    cpt_cis:       'Manual CPT / CIS Allocation & Deduction',
    season:        'Season Reward Management',
    season_payout: 'Season Payouts — Snapshot, Tiers & Airdrop',
    staking:       'Staking Management',
    revenue:       'Platform Revenue',
    referrals:     'Referral Analytics',
    offerwall:     'Offerwall Management',
    bounty:        'Bounty Campaign Management',
    ads:           'Ad Manager',
    campaigns:     'Campaign Performance Analytics',
    moderation:    'Community Moderation Tools',
    announcements: 'Announcement Management',
    categories:    'Categories',
    audit:         'User Activity Audit Logs',
    config:        'Platform Configuration Settings',
    team:          'Admin Team',
    policies:      'Policy Editor',
  }

  return (
    <div className="min-h-screen bg-surface-container flex">
      {idleWarning && <IdleWarning countdown={idleCountdown} onStay={stayActive} onLogout={logout} />}
      {showInvite  && <InviteModal onClose={() => { setShowInvite(false); setTeamRefreshKey(k => k + 1) }} />}

      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col h-screen w-72 bg-surface sticky top-0 z-40 overflow-y-auto border-r-4 border-on-background">
        <div className="flex flex-col h-full">
          <div className="px-6 py-6 border-b-4 border-on-background">
            <div className="flex items-center gap-3">
              <img src="/favicon.jpg" alt="Chicago" className="w-9 h-9 object-cover border-4 border-on-background" />
              <div>
                <h1 className="font-extrabold text-[18px] uppercase tracking-tight leading-none">Chicago</h1>
                <p className="font-bold text-[9px] uppercase tracking-[0.16em] text-on-surface-variant">Admin Console</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 py-2">
            {tabs.map(t => (
              <NavItem key={t.id} icon={t.icon} label={t.label} active={activeTab === t.id}
                onClick={() => setActiveTab(t.id)} />
            ))}
          </nav>
          <div className="border-t-4 border-on-background">
            <NavItem icon="logout"   label="Log Out"  active={false} onClick={logout}   />
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-surface border-b-4 border-on-background px-6 lg:px-10 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button className="md:hidden p-1 text-on-surface" onClick={() => setMobileNavOpen(v => !v)}>
              <span className="material-symbols-outlined">menu</span>
            </button>
            <h2 className="font-extrabold text-[18px] uppercase tracking-tight">{tabTitles[activeTab]}</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden lg:block" />
            <button
              onClick={toggleTheme}
              className="relative p-2"
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <span className="material-symbols-outlined text-on-surface">
                {dark ? 'light_mode' : 'dark_mode'}
              </span>
            </button>
            <button onClick={() => setShowInvite(true)}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-primary-container text-on-primary-fixed font-bold text-[11px] uppercase tracking-wider border-2 border-on-background"
              style={{ boxShadow: '2px 2px 0px 0px var(--neo-border-color)' }}>
              <span className="material-symbols-outlined text-[16px]">person_add</span>
              Invite Admin
            </button>
            <button onClick={logout}
              className="flex items-center gap-2 px-4 py-2 font-bold text-[11px] uppercase tracking-wider border-2 border-on-background">
              <span className="material-symbols-outlined text-[16px]">logout</span>
              <span className="hidden sm:block">Log Out</span>
            </button>
          </div>
        </header>

        {/* Mobile slide-in nav — always mounted, toggled via translate */}
        <div className={`md:hidden fixed inset-0 z-50 transition-opacity duration-200 ${mobileNavOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'} bg-on-background/60`}
          onClick={() => setMobileNavOpen(false)}>
          <div className={`w-72 h-full bg-surface border-r-4 border-on-background flex flex-col fixed top-0 left-0 transition-transform duration-200 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`}
            onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b-4 border-on-background flex items-center justify-between flex-shrink-0">
              <span className="font-extrabold text-[18px] uppercase tracking-tight">Chicago Admin</span>
              <button onClick={() => setMobileNavOpen(false)}><span className="material-symbols-outlined">close</span></button>
            </div>
            <nav className="flex-1 py-2 overflow-y-auto">
              {tabs.map(t => (
                <NavItem key={t.id} icon={t.icon} label={t.label} active={activeTab === t.id}
                  onClick={() => { setActiveTab(t.id); setMobileNavOpen(false) }} />
              ))}
            </nav>
            <div className="border-t-4 border-on-background flex-shrink-0">
              <NavItem icon="logout" label="Log Out" active={false} onClick={logout} />
            </div>
          </div>
        </div>

        <main className="flex-1 p-6 lg:p-10 max-w-[1400px] mx-auto w-full">
          <div className="flex items-center gap-2 mb-6">
            <span className="font-bold text-[10px] uppercase tracking-[0.16em] text-on-surface-variant">Admin</span>
            <span className="text-on-surface-variant/40 font-bold">/</span>
            <span className="font-bold text-[10px] uppercase tracking-[0.16em] text-on-surface">{tabTitles[activeTab]}</span>
          </div>
          {activeTab === 'overview'      && <Overview onInvite={() => setShowInvite(true)} />}
          {activeTab === 'users'         && <UserManagement />}
          {activeTab === 'cpt_cis'       && <CptCisAllocation />}
          {activeTab === 'season'        && <SeasonRewards />}
          {activeTab === 'season_payout' && <SeasonLeaderboardManagement />}
          {activeTab === 'staking'       && (
            <div className="flex flex-col gap-8">
              <StakingManagement />
              <StakingSettings />
            </div>
          )}
          {activeTab === 'revenue'       && <PlatformRevenue />}
          {activeTab === 'referrals'     && <ReferralAnalytics />}
          {activeTab === 'offerwall'     && <OfferwallManagement />}
          {activeTab === 'social_tasks'  && <TaskManagement />}
          {activeTab === 'bounty'       && <BountyManagement />}
          {activeTab === 'ads'           && <AdManager />}
          {activeTab === 'campaigns'     && <CampaignAnalytics />}
          {activeTab === 'moderation'    && <CommunityModeration />}
          {activeTab === 'announcements' && <Announcements />}
          {activeTab === 'categories'    && <Categories />}
          {activeTab === 'audit'         && <AuditLogManagement />}
          {activeTab === 'config'        && <PlatformConfig />}
          {activeTab === 'team'          && <AdminTeam key={teamRefreshKey} onInvite={() => setShowInvite(true)} />}
          {activeTab === 'policies'      && <PolicyEditor />}
        </main>
      </div>

    </div>
  )
}
